"use client";

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { gsap, ScrollTrigger } from "../../lib/gsap";
import { Heading, type HeadingProps } from "@repo/ui/heading";
import {
  BlocksRenderer,
  type BlocksContent,
} from "@strapi/blocks-react-renderer";
import { BaseImage, absoluteCmsUrl } from "../base-image/BaseImage";

/* =========================
   Types to match your response
   ========================= */
type StrapiImageFormat = {
  url?: string | null;
  width?: number;
  height?: number;
};
type StrapiMedia = {
  id: number;
  url?: string | null;
  name?: string;
  alternativeText?: string | null;
  caption?: string | null;
  formats?: {
    large?: StrapiImageFormat;
    medium?: StrapiImageFormat;
    small?: StrapiImageFormat;
    thumbnail?: StrapiImageFormat;
  };
};

type AccordionItem = {
  id: number;
  title: string;
  description?: BlocksContent | null;
};

type SectionHeading = {
  id: number;
  title: string;
  level: HeadingProps["level"];
};

export type AccordionsWithImagesProps = {
  heading?: SectionHeading | null;
  accordions: AccordionItem[];
  images: StrapiMedia[]; // single image pool used for the floating frames
};

/* =========================
   Helpers
   ========================= */
const bestUrl = (m?: StrapiMedia) =>
  m?.formats?.large?.url ||
  m?.formats?.medium?.url ||
  m?.formats?.small?.url ||
  m?.url ||
  null;

export default function AccordionsWithImages({
  heading,
  accordions = [],
  images = [],
}: AccordionsWithImagesProps) {
  const [openIndex, setOpenIndex] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const framesRef = useRef<(HTMLDivElement | null)[]>([]);

  // Prepare a normalized list of images (absolute URLs + alt)
  const preparedImages = useMemo(() => {
    return images
      .map((img) => {
        const url = bestUrl(img);
        if (!url) return null;
        return {
          id: img.id,
          src: absoluteCmsUrl(url),
          alt: img.alternativeText || img.caption || img.name || "Image",
        };
      })
      .filter(Boolean) as { id: number; src: string; alt: string }[];
  }, [images]);

  // Fixed positions (from your previous pattern). Adjust as you like.
  const positions = useMemo(
    () => [
      { top: "5%", left: "0%", width: 174, height: 265 },
      { top: "22%", left: "5%", width: 204, height: 136 },
      { bottom: "28%", left: "0%", width: 283, height: 189 },
      { bottom: "15%", left: "10%", width: 236, height: 157 },
      { top: "5%", right: "1%", width: 283, height: 188 },
      { top: "28%", right: "15%", width: 170, height: 259 },
      { bottom: "20%", right: "20%", width: 122, height: 186 },
      { bottom: "8%", right: "0%", width: 283, height: 189 },
    ],
    [],
  );

  // Parallax/float like your previous component
  useEffect(() => {
    framesRef.current.forEach((el, i) => {
      if (!el) return;
      // Add GPU acceleration class
      el.classList.add("parallax-element");

      gsap.to(el, {
        y: i % 2 === 0 ? -50 : 50,
        scrollTrigger: {
          trigger: containerRef.current!,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.5, // Slightly smoother scrub
          fastScrollEnd: true, // Better performance
        },
      });
    });
  }, [positions.length]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const { clientX, clientY } = e;
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;

    framesRef.current.forEach((el, i) => {
      if (!el) return;
      const dx = ((clientX - cx) / cx) * (i % 2 === 0 ? -3 : 3);
      const dy = ((clientY - cy) / cy) * (i % 2 === 0 ? -3 : 3);
      gsap.to(el, {
        x: dx,
        y: `+=${dy}`,
        duration: 0.4, // Slightly faster
        ease: "power2.out",
        force3D: true, // Force GPU acceleration
      });
    });
  }, []);

  // Only one open: clicking an already-open one collapses it (so zero open), otherwise opens the new one
  const toggle = (idx: number) => {
    setOpenIndex((prev) => (prev === idx ? -1 : idx));
  };

  if (!accordions.length) return null;

  return (
    <section
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className="relative reveal-stagger overflow-hidden px-4 md:mt-24 md:pt-44 md:pb-44 py-24 md:min-h-[900px]"
      aria-label="Mission & vision"
    >
      {/* Center block */}
      <div className="max-w-xl mx-auto text-center">
        {heading?.title && (
          <Heading
            level={heading.level}
            className="xl:text-6xl md:text-5xl text-4xl font-normal tracking-tighter leading-tight reveal-headline"
          >
            {heading.title}
          </Heading>
        )}

        <ul
          className="md:mt-24 mt-10 flex flex-col items-stretch"
          role="list"
          aria-label="Accordion list"
        >
          {accordions.map((item, idx) => {
            const panelId = `accordion-panel-${item.id}`;
            const btnId = `accordion-trigger-${item.id}`;
            const isOpen = openIndex === idx;

            return (
              <li key={item.id} className="w-full flex flex-col gap-6">
                <hr
                  className="border-gray-200 reveal-item"
                  aria-hidden="true"
                />
                <h3 className="text-xl md:text-2xl reveal-item">
                  <button
                    id={btnId}
                    type="button"
                    onClick={() => toggle(idx)}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    className="w-full cursor-pointer tracking-tighter text-black hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-black rounded-sm py-2"
                  >
                    {item.title}
                  </button>
                </h3>

                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={btnId}
                  className={`grid reveal-item place-items-center overflow-hidden transition-all duration-300 ${isOpen ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="min-h-0">
                    {item.description && (
                      <div className="mx-auto max-w-2xl text-gray-600 text-sm md:text-base leading-relaxed">
                        <BlocksRenderer content={item.description} />
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
          <hr className="border-gray-200" aria-hidden="true" />
        </ul>
      </div>

      {/* Floating image frames (decorative) */}
      {positions.map((style, i) => {
        const img = preparedImages[i % Math.max(preparedImages.length || 1, 1)];
        if (!img) return null;
        return (
          <div
            key={`position-${i}`}
            ref={(el) => {
              framesRef.current[i] = el;
            }}
            className="absolute hidden md:block overflow-hidden rounded"
            style={{
              ...style,
              width: `${style.width}px`,
              height: `${style.height}px`,
            }}
            aria-hidden="true"
          >
            <BaseImage
              src={img.src}
              alt={img.alt}
              width={style.width}
              height={style.height}
              className="h-full reveal-item w-full object-cover"
              decoding="async"
              loading={i < 2 ? "eager" : "lazy"}
            />
          </div>
        );
      })}
    </section>
  );
}
